/**
 * Multi-image uploader for galleries (drop hero + additional photos).
 * Reuses single-file ImageUploader for the upload pipeline. Stores an
 * ordered array of S3 URLs. Drag-to-reorder, remove, and visual hero highlight.
 */
import { useState } from "react";
import ImageUploader from "@/components/ImageUploader";
import { X, ArrowLeft, ArrowRight, Star } from "lucide-react";

export default function MultiImageUploader({ value = [], onChange, folder = "drops", testId = "multi-img" }) {
  const images = Array.isArray(value) ? value : [];
  const [adding, setAdding] = useState(false);

  const add = (url) => {
    if (!url) return;
    onChange([...images, url]);
    setAdding(false);
  };
  const remove = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= images.length) return;
    const next = images.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid={testId} className="space-y-3">
      {images.length === 0 && !adding && (
        <div className="text-sm text-[var(--ff-text-muted)] italic">No photos yet.</div>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={`${testId}-list`}>
          {images.map((url, idx) => (
            <div key={url + idx} className="relative group border border-[var(--ff-border)] rounded-lg overflow-hidden bg-white" data-testid={`${testId}-item-${idx}`}>
              <img src={url} alt="" className="w-full h-32 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
              {idx === 0 && (
                <span className="absolute top-2 left-2 bg-amber-100 text-amber-900 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                  <Star size={10}/> Hero
                </span>
              )}
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => remove(idx)} className="bg-white/95 hover:bg-white text-rose-700 rounded p-1 shadow-sm" aria-label="Remove" data-testid={`${testId}-remove-${idx}`}>
                  <X size={12}/>
                </button>
              </div>
              <div className="absolute bottom-1 left-1 right-1 flex justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="bg-white/95 hover:bg-white rounded p-1 shadow-sm disabled:opacity-30" aria-label="Move left" data-testid={`${testId}-left-${idx}`}>
                  <ArrowLeft size={12}/>
                </button>
                <button type="button" disabled={idx === images.length - 1} onClick={() => move(idx, 1)} className="bg-white/95 hover:bg-white rounded p-1 shadow-sm disabled:opacity-30" aria-label="Move right" data-testid={`${testId}-right-${idx}`}>
                  <ArrowRight size={12}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="border border-[var(--ff-border)] rounded-lg p-3 bg-[var(--ff-muted)]">
          <ImageUploader value="" onChange={add} folder={folder} testId={`${testId}-uploader`}/>
          <button type="button" onClick={() => setAdding(false)} className="text-xs text-[var(--ff-text-muted)] hover:underline mt-2" data-testid={`${testId}-cancel-add`}>Cancel</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="btn-secondary text-sm w-full"
          data-testid={`${testId}-add-btn`}
        >
          + Add photo
        </button>
      )}
      <p className="text-[11px] text-[var(--ff-text-muted)]">First photo is the hero shown in card view. Drag arrows to reorder.</p>
    </div>
  );
}
